import { useCallback, useEffect, useState } from "react";
import { Alan, Buton, cn, Iskelet, Kart, MetinAlani, Rozet, Secim } from "@koclukpanel/ui";
import {
  ApiHatasi,
  destekAc,
  destekGetir,
  destekListem,
  destekMesajEkle,
  talepAc,
  talepKategorileri,
  taleplerim,
  type DestekKonusu,
  type DestekMesaji,
  type DestekOzeti,
  type OnayDurumu,
  type TalepKategorisi,
  type Talebim,
} from "./api";

/**
 * TALEP KUTUSU — ogrenci talebi (§5.8) ve destek talebi (§5.15)
 *
 * Iki ayri sey ve ekranda da AYRI duruyorlar:
 *   Talep  -> kuruma bir sey soruyorsun (uzman degisikligi, kaynak)
 *   Destek -> SISTEM takiliyor (ekran acilmiyor, buton calismiyor)
 *
 * Tek listeye karistirsaydik "uzmanimi degistirin" ile "uygulama
 * donuyor" ayni kuyruga duserdi; ikisi farkli kisilere gidiyor ve
 * farkli surelerde cevaplaniyor.
 */

const DURUM_ADI: Record<OnayDurumu, string> = {
  PENDING: "Yanıt bekleniyor",
  IN_REVIEW: "İnceleniyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal edildi",
};

const DURUM_SINIFI: Record<OnayDurumu, string> = {
  PENDING: "bg-uyari-500/15 text-uyari-600",
  IN_REVIEW: "bg-uyari-500/15 text-uyari-600",
  APPROVED: "bg-basarili-500/15 text-onay-700",
  REJECTED: "bg-hata-500/15 text-hata-700",
  CANCELLED: "bg-notr-100 text-notr-600",
};

const KONU_ADI: Record<DestekKonusu, string> = {
  TECHNICAL: "Bir şey çalışmıyor",
  HOW_TO: "Nasıl yapılır bilmiyorum",
  BUG: "Hata var gibi",
  SUGGESTION: "Önerim var",
  OTHER: "Diğer",
};

/** Sunucudaki alt sinirlar — form onceden uyarsin diye. */
const EN_AZ_KONU = 3;
const EN_AZ_GOVDE = 10;

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// F-PAR-10b — çocuk seçimi veli kabuğundan gelir; yetki sunucuda doğrulanır.
export function TalepKutusu({ ogrenciId }: { ogrenciId?: string } = {}) {
  const [gorunum, setGorunum] = useState<"talep" | "destek">("talep");

  return (
    <div className="flex flex-col gap-4 p-4">
      {/*
        `role="tab"` DEĞİL: ok tuşu gezinmesi, gezinen odak ve `tabpanel`
        bağı olmadan ARIA sekme deseni yarım kalır ve yarım desen hiç
        yazmamaktan kötüdür. Gerekçe: `packages/ui/src/Sekmeler.tsx`.
      */}
      <div role="group" aria-label="Talep görünümü" className="flex gap-2">
        {(
          [
            ["talep", "İsteklerim"],
            ["destek", "Yardım"],
          ] as const
        ).map(([anahtar, etiket]) => (
          <button
            key={anahtar}
            type="button"
            aria-pressed={gorunum === anahtar}
            onClick={() => setGorunum(anahtar)}
            className={cn(
              "min-h-dokunma rounded-alan px-4 text-sm font-medium",
              "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
              gorunum === anahtar ? "bg-marka-600 text-notr-0" : "bg-notr-100 text-notr-700",
            )}
          >
            {etiket}
          </button>
        ))}
      </div>

      {gorunum === "talep" ? <Istekler ogrenciId={ogrenciId} /> : <Yardim />}
    </div>
  );
}

// ---------------------------------------------------------------
// ISTEKLER
// ---------------------------------------------------------------

function Istekler({ ogrenciId }: { ogrenciId?: string }) {
  const [kategoriler, setKategoriler] = useState<TalepKategorisi[]>([]);
  const [talepler, setTalepler] = useState<Talebim[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);

  const [formAcik, setFormAcik] = useState(false);
  const [kategoriId, setKategoriId] = useState("");
  const [konu, setKonu] = useState("");
  const [govde, setGovde] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderimHatasi, setGonderimHatasi] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const [k, t] = await Promise.all([talepKategorileri(), taleplerim(ogrenciId)]);
      /*
        ================================================================
        BURADA SÜZME YOK — VE BU BİLİNÇLİ
        ================================================================
        04.09.2026'da ekran öğrencinin AÇAMAYACAĞI kategorileri de
        çiziyordu (canlı ölçüldü: altı geliyordu, ikisi veli-özel;
        öğrenci "Ödeme erteleme"yi seçip yazdıktan sonra sunucudan
        `KATEGORI_KAPALI` yiyordu). İlk düzeltme burada bir süzgeçti.

        Sonra kural SUNUCUYA alındı (`talepKategorileri` artık
        `kategoriAcilabilirMi` ile süzüyor) ve istemci süzgeci
        KALDIRILDI. Gerekçe CLAUDE.md'nin kendi cümlesi: *"API-first:
        hiçbir iş kuralı arayüzde yaşamaz. Arayüz sunucuya sorar."*

        Bir sayıyı sözleşmeye taşımak başka, bir KARARI istemciye
        kopyalamak başka. Kural sunucuda zaten vardı ve açma yolunda
        doğru çalışıyordu; istemciye kopyalamak onu ikinci bir eve
        taşımak olurdu — bu dosyanın bugün öğrendiği şeyin tam tersi.

        Buraya süzgeç EKLEME. Liste eksik ya da fazla geliyorsa hata
        `operasyon.service.ts` içindedir.
      */
      setKategoriler(k);
      setTalepler(t.talepler);
      /*
        Ilk kategori secili gelsin: bos birakmak, formu acan ogrencinin
        once bir acilir kutuyla ugrasmasini gerektirirdi.

        Islevsel guncelleme kullaniliyor — `kategoriId`yi okumak bu
        fonksiyonu ona BAGIMLI yapardi ve o zaman her secim
        degisikliginde liste yeniden cekilirdi.
      */
      setKategoriId((mevcut) => mevcut || (k[0]?.id ?? ""));
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Bilgiler alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, [ogrenciId]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const gonder = async () => {
    setGonderiliyor(true);
    setGonderimHatasi(null);
    try {
      await talepAc({
        ...(ogrenciId ? { ogrenciId } : {}),
        kategoriId,
        konu: konu.trim(),
        govde: govde.trim(),
      });
      setKonu("");
      setGovde("");
      setFormAcik(false);
      await yukle();
    } catch (err) {
      setGonderimHatasi(err instanceof ApiHatasi ? err.message : "İstek gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  if (yukleniyor && talepler.length === 0 && kategoriler.length === 0) {
    return <Iskelet satir={4} />;
  }

  if (hata) {
    return (
      <div className="flex flex-col gap-3">
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
        <Buton gorunum="ikincil" onClick={() => void yukle()}>
          Tekrar dene
        </Buton>
      </div>
    );
  }

  const eksik = konu.trim().length < EN_AZ_KONU || govde.trim().length < EN_AZ_GOVDE;
  const secili = kategoriler.find((k) => k.id === kategoriId);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-notr-600 text-sm">
        Kuruma talebinizi buradan iletebilirsiniz. Koçunuz ya da yönetim yanıtlar.
      </p>

      {!formAcik && (
        <Buton boyut="genis" onClick={() => setFormAcik(true)}>
          Yeni istek
        </Buton>
      )}

      {formAcik && (
        <form
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!eksik && kategoriId) void gonder();
          }}
        >
          {/*
            HAM `<select>` YERİNE `Secim` — bu bir KAYIT FORMU.
            `Secim`in gerekçesi süzgeç kutularının toplu çevrilmemesini
            söylüyor; eksik olan hata durumu ise "kayıt formlarında
            gerekiyor" diyor. Burası o istisna.
          */}
          <Secim
            etiket="Konu başlığı"
            value={kategoriId}
            onChange={(e) => setKategoriId(e.target.value)}
          >
            {kategoriler.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </Secim>

          {/*
            Kategori aciklamasi VARSA gosteriliyor: "uzman degisikligi"
            gibi bir baslikta ogrencinin ne beklemesi gerektigini
            bilmesi, hayal kirikligini onceden onluyor.
          */}
          {secili?.description && <p className="text-notr-600 text-xs">{secili.description}</p>}

          <Alan
            etiket="Kısa başlık"
            value={konu}
            onChange={(e) => setKonu(e.target.value)}
            maxLength={200}
          />

          <MetinAlani
            etiket="Ne istiyorsun?"
            value={govde}
            onChange={(e) => setGovde(e.target.value)}
            rows={4}
            maxLength={5000}
          />

          <div className="flex gap-2">
            <Buton type="submit" disabled={eksik || !kategoriId || gonderiliyor}>
              {gonderiliyor ? "Gönderiliyor…" : "Gönder"}
            </Buton>
            <Buton gorunum="sessiz" type="button" onClick={() => setFormAcik(false)}>
              Vazgeç
            </Buton>
          </div>

          {gonderimHatasi && (
            <p role="alert" className="text-hata-500 text-sm">
              {gonderimHatasi}
            </p>
          )}
        </form>
      )}

      <section className="flex flex-col gap-2">
        {/*
          ÇİZİMDE "Geçmiş istekler" ve 15px. "İsteklerin" kısaydı ama
          neyi listelediğini söylemiyordu: açık istekle kapanmışı ayıran
          şey listenin adı değil, satırdaki durum rozeti.

          ÇİZİMDEKİ "Yanıt süresi ortalama 4 saat" YAZILMADI. Sistem
          yanıt süresini ÖLÇMÜYOR; ölçülmeyen bir söz ekrana yazılırsa
          altı saat bekleyen öğrenci sistemin bozuk olduğunu düşünür.
          Ölçüm eklendiğinde cümle de gelir.
        */}
        <h2 className="text-notr-900 text-[15px] font-semibold">Geçmiş istekler</h2>
        {talepler.length === 0 ? (
          <p className="text-notr-600 text-sm">Henüz bir istek göndermedin.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {talepler.map((t) => (
              <Kart key={t.id} olarak="li" className="p-3">
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="text-notr-900 block text-sm font-medium">{t.subject}</span>
                    <span className="text-notr-600 block text-xs">
                      {t.category.name} · {zaman(t.createdAt)}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                      DURUM_SINIFI[t.status],
                    )}
                  >
                    {DURUM_ADI[t.status]}
                  </span>
                </div>

                {/*
                  Karar notu VARSA gosteriliyor. Reddedilen bir istekte
                  gerekce gormemek, ogrenciyi ayni istegi tekrar
                  yazmaya iter ve sistem "sessizce hayir diyen" bir sey
                  olur.
                */}
                {t.decisionNote && (
                  <p className="rounded-alan bg-notr-50 text-notr-700 mt-2 p-2 text-sm">
                    {t.decisionNote}
                  </p>
                )}
              </Kart>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------
// YARDIM / DESTEK
// ---------------------------------------------------------------

function Yardim() {
  const [talepler, setTalepler] = useState<DestekOzeti[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<string | null>(null);

  const [formAcik, setFormAcik] = useState(false);
  const [konu, setKonu] = useState<DestekKonusu>("HOW_TO");
  const [baslik, setBaslik] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderimHatasi, setGonderimHatasi] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      const c = await destekListem();
      setTalepler(c.talepler);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Talepler alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const gonder = async () => {
    setGonderiliyor(true);
    setGonderimHatasi(null);
    try {
      /*
        `ekranAnahtari` GONDERILIYOR: "en cok soru gelen ekranlar"
        raporu buna dayaniyor (matris §5.15). Gonderilmeseydi rapor
        her zaman bos cikar ve hangi ekranin kullanicilari
        zorladigini kimse ogrenemezdi.
      */
      await destekAc({
        konu,
        baslik: baslik.trim(),
        mesaj: mesaj.trim(),
        ekranAnahtari: "ogrenci-yardim",
      });
      setBaslik("");
      setMesaj("");
      setFormAcik(false);
      await yukle();
    } catch (err) {
      setGonderimHatasi(err instanceof ApiHatasi ? err.message : "Talep gönderilemedi.");
    } finally {
      setGonderiliyor(false);
    }
  };

  if (yukleniyor && talepler.length === 0) {
    return <Iskelet satir={4} />;
  }

  if (hata) {
    return (
      <div className="flex flex-col gap-3">
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
        <Buton gorunum="ikincil" onClick={() => void yukle()}>
          Tekrar dene
        </Buton>
      </div>
    );
  }

  const eksik = baslik.trim().length < EN_AZ_KONU || mesaj.trim().length < EN_AZ_GOVDE;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-notr-600 text-sm">
        Uygulamada bir şey çalışmıyorsa ya da nasıl yapılacağını bilmiyorsan buradan yaz.
      </p>

      {!formAcik && (
        <Buton boyut="genis" onClick={() => setFormAcik(true)}>
          Yardım iste
        </Buton>
      )}

      {formAcik && (
        <form
          className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col gap-3 border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!eksik) void gonder();
          }}
        >
          {/* Kayıt formu — gerekçesi yukarıdaki `Secim` notunda. */}
          <Secim
            etiket="Ne oldu?"
            value={konu}
            onChange={(e) => setKonu(e.target.value as DestekKonusu)}
          >
            {(Object.keys(KONU_ADI) as DestekKonusu[]).map((k) => (
              <option key={k} value={k}>
                {KONU_ADI[k]}
              </option>
            ))}
          </Secim>

          <Alan
            etiket="Kısa başlık"
            value={baslik}
            onChange={(e) => setBaslik(e.target.value)}
            maxLength={200}
          />

          <MetinAlani
            etiket="Ayrıntı"
            value={mesaj}
            onChange={(e) => setMesaj(e.target.value)}
            rows={4}
            maxLength={5000}
            placeholder="Ne yapmaya çalıştın, ne oldu?"
          />

          <div className="flex gap-2">
            <Buton type="submit" disabled={eksik || gonderiliyor}>
              {gonderiliyor ? "Gönderiliyor…" : "Gönder"}
            </Buton>
            <Buton gorunum="sessiz" type="button" onClick={() => setFormAcik(false)}>
              Vazgeç
            </Buton>
          </div>

          {gonderimHatasi && (
            <p role="alert" className="text-hata-500 text-sm">
              {gonderimHatasi}
            </p>
          )}
        </form>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-notr-900 text-sm font-semibold">Yardım taleplerin</h2>
        {talepler.length === 0 ? (
          <p className="text-notr-600 text-sm">Henüz talep açmadın.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {talepler.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setAcik(acik === t.id ? null : t.id)}
                  aria-expanded={acik === t.id}
                  className={cn(
                    "min-h-dokunma rounded-alan flex w-full items-center gap-3 px-3 text-left",
                    "bg-notr-0 ring-notr-200 ring-1",
                    "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="text-notr-900 block truncate text-sm font-medium">
                      {t.subject}
                    </span>
                    <span className="text-notr-600 block text-xs">
                      #{t.ticketNo} · {zaman(t.createdAt)}
                    </span>
                  </span>
                  {/*
                    "Yanitlandi" ogrenciye ACIKCA soylenmeli: cevabin
                    geldigini gormek icin her talebi tek tek acmak
                    zorunda kalmasin.
                  */}
                  {t.respondedAt && (
                    <Rozet ton="onay" className="shrink-0">
                      Yanıtlandı
                    </Rozet>
                  )}
                </button>

                {acik === t.id && <Yazisma destekId={t.id} kapali={t.status === "CLOSED"} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Yazisma({ destekId, kapali }: { destekId: string; kapali: boolean }) {
  const [mesajlar, setMesajlar] = useState<DestekMesaji[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [govde, setGovde] = useState("");
  const [hata, setHata] = useState<string | null>(null);

  const yukle = useCallback(async () => {
    try {
      const c = await destekGetir(destekId);
      setMesajlar(c.mesajlar);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Yazışma alınamadı.");
    } finally {
      setYukleniyor(false);
    }
  }, [destekId]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const gonder = async () => {
    setHata(null);
    try {
      await destekMesajEkle(destekId, govde.trim());
      setGovde("");
      await yukle();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Mesaj gönderilemedi.");
    }
  };

  if (yukleniyor) return <Iskelet satir={4} />;

  return (
    <Kart className="mt-2 flex flex-col gap-3 p-3">
      <ul className="flex flex-col gap-2">
        {mesajlar.map((m) => (
          <li
            key={m.id}
            className={cn(
              "rounded-alan p-2 text-sm",
              // `isStaff` SUNUCUDA izinden turetiliyor; istemciden
              // gelseydi ogrenci kendi mesajini "destek cevabi" gibi
              // isaretleyebilirdi.
              m.isStaff ? "bg-marka-50 text-notr-800" : "bg-notr-50 text-notr-700",
            )}
          >
            <span className="text-notr-600 block text-xs font-medium">
              {m.isStaff ? "Destek ekibi" : "Sen"} · {zaman(m.createdAt)}
            </span>
            <span className="mt-0.5 block whitespace-pre-wrap">{m.body}</span>
          </li>
        ))}
        {mesajlar.length === 0 && <li className="text-notr-600 text-sm">Henüz mesaj yok.</li>}
      </ul>

      {kapali ? (
        <p className="text-notr-600 text-sm">
          Bu talep kapatıldı. Yeni bir sorun için yeni talep aç.
        </p>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (govde.trim()) void gonder();
          }}
        >
          <MetinAlani
            etiket="Yanıt"
            value={govde}
            onChange={(e) => setGovde(e.target.value)}
            rows={2}
          />
          <div>
            <Buton type="submit" disabled={!govde.trim()}>
              Gönder
            </Buton>
          </div>
        </form>
      )}

      {hata && (
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
      )}
    </Kart>
  );
}
